import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiTarget, FiPenTool, FiRefreshCw, FiCheck, FiArrowRight, FiClock } = FiIcons;

const Process = () => {
  const processSteps = [
    {
      step: '01',
      title: 'Discover',
      icon: FiSearch,
      timeline: '1-2 weeks',
      description: 'We start by understanding your business, goals, and challenges through comprehensive research and consultation.',
      activities: [
        'Client consultation and brief development',
        'Market research and competitor analysis',
        'Target audience identification',
        'Brand audit and assessment',
        'Project scope definition'
      ],
      deliverables: [
        'Project brief',
        'Research report',
        'Audience personas',
        'Competitive analysis'
      ]
    },
    {
      step: '02',
      title: 'Strategy',
      icon: FiTarget,
      timeline: '1 week',
      description: 'Based on our research, we develop a comprehensive creative strategy that aligns with your business objectives.',
      activities: [
        'Creative strategy development',
        'Concept ideation and mood boards',
        'Technical requirements planning',
        'Content strategy outline',
        'Project timeline finalization'
      ],
      deliverables: [
        'Creative strategy document',
        'Mood boards',
        'Technical specifications',
        'Project roadmap'
      ]
    },
    {
      step: '03',
      title: 'Create',
      icon: FiPenTool,
      timeline: '2-4 weeks',
      description: 'Our creative team brings the strategy to life through innovative design concepts and compelling content.',
      activities: [
        'Initial design concepts',
        'Client presentation and feedback',
        'Iterative design development',
        'Content creation and copywriting',
        'Asset development'
      ],
      deliverables: [
        'Design concepts',
        'Presentation materials',
        'Content drafts',
        'Asset library'
      ]
    },
    {
      step: '04',
      title: 'Refine',
      icon: FiRefreshCw,
      timeline: '1-2 weeks',
      description: 'We incorporate your feedback and refine the designs to perfection, ensuring every detail meets your expectations.',
      activities: [
        'Client feedback integration',
        'Design refinement and optimization',
        'Quality assurance testing',
        'Performance optimization',
        'Final approval process'
      ],
      deliverables: [
        'Refined designs',
        'QA reports',
        'Performance metrics',
        'Final approvals'
      ]
    },
    {
      step: '05',
      title: 'Deliver',
      icon: FiCheck,
      timeline: '1 week',
      description: 'We deliver the final assets and provide comprehensive support to ensure successful implementation.',
      activities: [
        'Final asset delivery',
        'Implementation support',
        'Training and handover',
        'Documentation provision',
        'Ongoing support setup'
      ],
      deliverables: [
        'Final assets',
        'Brand guidelines',
        'Training materials',
        'Support documentation'
      ]
    }
  ];

  const benefits = [
    {
      title: 'Transparent Communication',
      description: 'Regular updates and clear communication throughout every phase of your project.',
      icon: FiTarget
    },
    {
      title: 'Collaborative Approach',
      description: 'We work closely with you as partners, incorporating your feedback at every step.',
      icon: FiSearch
    },
    {
      title: 'Quality Assurance',
      description: 'Rigorous testing and quality checks ensure exceptional results every time.',
      icon: FiCheck
    },
    {
      title: 'Timely Delivery',
      description: 'Structured timeline management ensures your project is delivered on schedule.',
      icon: FiClock
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-6">
              Our <span className="text-purple-600">Process</span>
            </h1>
            <p className="font-inter text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              We follow a proven 5-step creative process that ensures exceptional results and exceeds client expectations every time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-xl flex items-center justify-center mr-4">
                      <SafeIcon icon={step.icon} className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <span className="font-poppins font-bold text-4xl text-gray-200">
                        {step.step}
                      </span>
                      <h2 className="font-poppins font-bold text-3xl text-gray-900">
                        {step.title}
                      </h2>
                    </div>
                  </div>
                  <div className="flex items-center mb-4">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-purple-600 mr-2" />
                    <span className="font-inter font-medium text-purple-600">
                      Timeline: {step.timeline}
                    </span>
                  </div>
                  <p className="font-inter text-lg text-gray-600 mb-6 leading-relaxed">
                    {step.description}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-3">
                        Key Activities:
                      </h3>
                      <ul className="space-y-2">
                        {step.activities.map((activity, activityIndex) => (
                          <li key={activityIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="font-inter text-gray-600 text-sm">{activity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-3">
                        Deliverables:
                      </h3>
                      <ul className="space-y-2">
                        {step.deliverables.map((deliverable, deliverableIndex) => (
                          <li key={deliverableIndex} className="flex items-start">
                            <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                            <span className="font-inter text-gray-600 text-sm">{deliverable}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                {/* Visual */}
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative">
                    <div className="w-full h-80 bg-gradient-to-br from-purple-600 to-teal-600 rounded-2xl flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                          <SafeIcon icon={step.icon} className="w-12 h-12 text-white" />
                        </div>
                        <h3 className="font-poppins font-bold text-2xl mb-2">{step.title}</h3>
                        <p className="font-inter opacity-90">{step.timeline}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Why Our Process Works
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Our structured approach ensures consistent quality, clear communication, and exceptional results for every project.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 text-center shadow-lg card-hover"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="font-inter text-gray-600 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="font-inter text-lg text-gray-300 max-w-2xl mx-auto mb-8">
              Let's discuss your project and show you how our proven process can deliver exceptional results for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Process;